import React, { useState } from 'react';

// Material UI
import { Box, Typography, TextField, IconButton, Tabs, Tab } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { styled } from '@mui/material/styles';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import BlockIcon from '@mui/icons-material/Block';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// Components
import Tooltip from 'components/popups/Tooltip';

// Utility
import { formatPrice } from 'utility/numbers';
import { normalizeCharacters } from 'utility/strings';

export default function DiscountList(props) {
    const { discounts, onEdit } = props;

    const [category, setCategory] = useState('all');
    const [searchValue, setSearchValue] = useState('');

    const handleEdit = (params) => {
        const value = params.getValue(params.id, 'c2');

        if (value.code !== 'sys_discount') {
            // Don't allow user to edit system owned discount codes
            onEdit(params.id);
        }
    };

    let filteredDiscounts = []; // Discounts to display based on category and search filters applied

    // ***** Filter by Category ***** //

    const activeDiscounts = discounts.filter(
        (d) => d.visible && d.discount_code !== 'sys_discount',
    );

    const inactiveDiscounts = discounts.filter(
        (d) => !d.visible && d.discount_code !== 'sys_discount',
    );

    const depletedDiscounts = discounts.filter((d) => {
        if (d.discount_code === 'sys_discount') return false; // Exclude system discount code from depleted check
        const usesDepleted = d.qty_total_available && d.qty_used >= d.qty_total_available;
        const tixDepleted = d.disc_ticket_max && d.ticket_count >= d.disc_ticket_max;
        return usesDepleted || tixDepleted;
    });

    if (category === 'all') filteredDiscounts = discounts;
    if (category === 'active') filteredDiscounts = activeDiscounts;
    if (category === 'inactive') filteredDiscounts = inactiveDiscounts;
    if (category === 'depleted') filteredDiscounts = depletedDiscounts;

    // ***** Filter by Search Query ***** //

    if (searchValue.trim()) {
        const searchStr = normalizeCharacters(searchValue.trim()); // Normalize string to remove accents and diacritics

        const searchRegex = new RegExp(searchStr.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');

        filteredDiscounts = filteredDiscounts.filter((row) => {
            const discountCode = normalizeCharacters(row.discount_code);
            return searchRegex.test(discountCode);
        });
    }

    const rows = filteredDiscounts.map((d) => {
        const usesDepleted = d.qty_total_available && d.qty_used >= d.qty_total_available;
        const tixDepleted = d.disc_ticket_max && d.ticket_count >= d.disc_ticket_max;

        let status = d.visible ? 'active' : 'inactive';

        if (usesDepleted || tixDepleted) {
            status = 'depleted';
        }

        if (d.discount_code === 'sys_discount') {
            status = 'system';
        }

        return {
            id: d.disc_id,
            c1: { sort: d.visible, code: d.discount_code, status: status },
            c2: { sort: d.discount_code, code: d.discount_code },
            c3: { sort: null, percent: d.disc_order_pct, amount: d.disc_order_amt },
            c4: { sort: d.disc_order_total, total: d.disc_order_total },
            c5: {
                sort: d.qty_used,
                used: d.qty_used,
                total: d.qty_total_available,
                depleted: usesDepleted,
            },
            c6: {
                sort: d.ticket_count,
                count: d.ticket_count,
                max: d.disc_ticket_max,
                depleted: tixDepleted,
            },
            // *** Hidden until we fix the query that sums up the scan count *** //
            // c7: { sort: d.scan_count,       scans: d.scan_count,       total: d.ticket_count        },
            // c8: { sort: d.scan_pct,         percent: d.scan_pct                                     },
            // c9: { sort: d.scan_pct,         percent: d.scan_pct                                     },
        };
    });

    const columns = [
        {
            field: 'c1',
            headerName: ' ',
            minWidth: 60,
            maxWidth: 60,
            align: 'center',
            hideSortIcons: true,
            renderCell: (params) => {
                if (params.value.status === 'active') {
                    return (
                        <Tooltip color="green" message="Active">
                            <CheckCircleOutlinedIcon color="success" />
                        </Tooltip>
                    );
                }
                if (params.value.status === 'inactive') {
                    return (
                        <Tooltip color="grey" message="Inactive">
                            <BlockIcon color="action" />
                        </Tooltip>
                    );
                }
                if (params.value.status === 'depleted') {
                    return (
                        <Tooltip color="red" message="Depleted">
                            <ErrorOutlineIcon color="error" />
                        </Tooltip>
                    );
                }
                if (params.value.status === 'system') {
                    return <AdminIcon />;
                }
            },
            sortComparator: (v1, v2, param1, param2) => {
                return param1.value.sort - param2.value.sort;
            },
        },
        {
            field: 'c2',
            headerName: 'CODE',
            minWidth: 180,
            maxWidth: 200,
            hideSortIcons: true,
            flex: 1,
            renderCell: (params) => {
                if (params.value.code === 'sys_discount') {
                    return (
                        <Box display="flex" alignItems={'center'}>
                            sys_discount
                            <Tooltip message="This is a system-generated discount code and is not available to customers. When you create an order from the admin portal, “sys_discount” is assigned to the complimentary order">
                                <InfoText>What's this?</InfoText>
                            </Tooltip>
                        </Box>
                    );
                } else {
                    return params.value.code;
                }
            },
            sortComparator: (v1, v2, param1, param2) => {
                return param1.value.sort
                    .toLowerCase()
                    .localeCompare(param2.value.sort.toLowerCase());
            },
        },
        {
            field: 'c3',
            headerName: 'VALUE',
            minWidth: 140,
            maxWidth: 160,
            hideSortIcons: true,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (params) =>
                params.value.percent
                    ? Number((params.value.percent * 100).toFixed(2)) + '% off'
                    : formatPrice(params.value.amount) + ' off',
        },
        {
            field: 'c4',
            headerName: 'DISCOUNTED',
            minWidth: 150,
            maxWidth: 150,
            hideSortIcons: true,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (params) => formatPrice(params.value.total),
            sortComparator: (v1, v2, param1, param2) => {
                return param1.value.sort - param2.value.sort;
            },
        },
        {
            field: 'c5',
            headerName: 'USES',
            minWidth: 120,
            maxWidth: 140,
            hideSortIcons: true,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => (
                <Box
                    sx={
                        params.value.depleted
                            ? { fontWeight: 'bold', color: 'error.main' }
                            : undefined
                    }
                >
                    {params.value.used} / {params.value.total ?? '∞'}
                </Box>
            ),
            sortComparator: (v1, v2, param1, param2) => {
                return param1.value.sort - param2.value.sort;
            },
        },
        {
            field: 'c6',
            headerName: 'TIX',
            minWidth: 120,
            maxWidth: 140,
            hideSortIcons: true,
            align: 'right',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box
                    sx={
                        params.value.depleted
                            ? { fontWeight: 'bold', color: 'error.main' }
                            : undefined
                    }
                >
                    {params.value.count} / {params.value.max ?? '∞'}
                </Box>
            ),
            sortComparator: (v1, v2, param1, param2) => {
                return param1.value.sort - param2.value.sort;
            },
        },
        // *** Hidden until we fix the query that sums up the scan count *** //
        // { field: 'c7', headerName: 'CHECKINS',      minWidth: 110,  maxWidth: 130,  hideSortIcons: true, align: 'right', headerAlign: 'right',
        //     valueFormatter: (params) => `${params.value.scans} / ${params.value.total ?? '-'}`,
        //     sortComparator: (v1, v2, param1, param2) => {
        //         return param1.value.sort - param2.value.sort;
        //     }
        // },
        // { field: 'c8', headerName: ' ',             minWidth: 60,   maxWidth: 80,   hideSortIcons: true, align: 'right', headerAlign: 'right',
        //     valueFormatter: (params) => `${params.value.percent}%`},
        // { field: 'c9', headerName: ' ',             minWidth: 120,  maxWidth: 120,  hideSortIcons: true, flex: 1,
        //     renderCell: (params) => <ProgressBar variant='determinate' color='secondary' value={params.value.percent} />
        // },
    ];

    return (
        <Root height={'100%'}>
            <Tabs value={category} onChange={(_, value) => setCategory(value)}>
                <Tab value="all" label={`All (${discounts.length})`} />
                <Tab
                    value="active"
                    label={
                        <Box display="flex" alignItems={'center'}>
                            <CheckCircleOutlinedIcon fontSize="small" />
                            <Typography variant="button" ml={1}>
                                ACTIVE ({activeDiscounts.length})
                            </Typography>
                        </Box>
                    }
                />
                <Tab
                    value="inactive"
                    label={
                        <Box display="flex" alignItems={'center'}>
                            <BlockIcon fontSize="small" />
                            <Typography variant="button" ml={1}>
                                INACTIVE ({inactiveDiscounts.length})
                            </Typography>
                        </Box>
                    }
                />
                <Tab
                    value="depleted"
                    label={
                        <Box display="flex" alignItems={'center'}>
                            <ErrorOutlineIcon fontSize="small" />
                            <Typography variant="button" ml={1}>
                                DEPLETED ({depletedDiscounts.length})
                            </Typography>
                        </Box>
                    }
                />
            </Tabs>
            <DG
                components={{ Toolbar: Toolbar }}
                componentsProps={{
                    toolbar: {
                        value: searchValue,
                        onChange: (event) => setSearchValue(event.target.value),
                        clearSearch: () => setSearchValue(''),
                    },
                }}
                onRowClick={handleEdit}
                disableSelectionOnClick
                rows={rows}
                columns={columns}
                disableColumnMenu
                getRowClassName={(params) =>
                    `discount-row--${params.getValue(params.id, 'c2').code === 'sys_discount' ? 'system' : 'user'}`
                }
                sortingOrder={['desc', 'asc']}
            />
        </Root>
    );
}

function Toolbar(props) {
    const { value, onChange, clearSearch } = props;

    return (
        <SearchBar
            autoFocus
            variant="outlined"
            size="small"
            value={value}
            onChange={onChange}
            placeholder="Search..."
            InputProps={{
                startAdornment: <SearchIcon fontSize="small" />,
                endAdornment: (
                    <IconButton
                        title="Clear"
                        aria-label="Clear"
                        size="small"
                        style={{ visibility: value ? 'visible' : 'hidden' }}
                        onClick={clearSearch}
                    >
                        <ClearIcon fontSize="small" />
                    </IconButton>
                ),
            }}
        />
    );
}

const DG = styled(DataGrid)(({ theme }) => ({
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus': {
        outline: 'none',
    },
}));

const AdminIcon = styled(SupervisorAccountIcon)(({ theme }) => ({
    height: '1.4rem',
    width: '1.4rem',
    color: theme.palette.grey[500],
}));

// *** Hidden until we fix the query that sums up the scan count *** //
// const ProgressBar = styled(LinearProgress)(({ theme }) => ({
//     height: 12,
//     backgroundColor: theme.palette.grey[300],
//     width: '100%'
// }));

const InfoText = styled(Typography)(({ theme }) => ({
    color: theme.palette.info.main,
    textDecoration: 'underline',
    marginLeft: theme.spacing(1),
    fontWeight: 'bold',
    fontSize: 12,
    marginTop: '1px',
}));
InfoText.defaultProps = { variant: 'body2' };

const Root = styled(Box)(({ theme }) => ({
    '& .discount-row--user': {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            cursor: 'pointer',
        },
    },
    '& .discount-row--system': {
        backgroundColor: theme.palette.grey[100],
        '&:hover': {
            backgroundColor: theme.palette.grey[100],
        },
    },
}));

const SearchBar = styled(TextField)(({ theme }) => ({
    margin: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '360px',
}));
